import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { Modal } from "src/ui/shared/Modal/Modal";

import { deleteGroup } from "@dashboard/devices/store/index";
import { Group } from "@dashboard/devices/types/index";
import { useCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { useLicenses } from "@dashboard/licenses/api/helpers";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { List, ListItem, Typography } from "@mui/material";
import { useCurrentWorkspaceProducts } from "@products/api/helpers";
import { CircularProgressBox } from "@shared/CircularProgressBox";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { DangerButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

type FormValues = {
    name: string;
    description: string;
};
type Props = {
    group: Group;
    open: boolean;
    onClose: () => void;
};

const DeleteGroupDialog = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const dispatch = useDispatch();

    const {
        data,
        isLoading: isLoadingFiles,
        isError: isFilesError,
    } = useCurrentWorkspaceFiles({ groups: [props.group.id], includeParentFiles: false }, { skip: !props.open });

    const { data: products = [], isLoading: isLoadingProducts, isError: isProductsError } = useCurrentWorkspaceProducts();
    const { data: workspace } = useCurrentWorkspace();

    const groupProducts = products.filter((product) => product.groupId === props.group.id);

    const licensesLength = props.group.licenseIds.length;
    const productsLength = groupProducts.length;
    const filesLength = data?.count || 0;
    const isGroupEmpty = !licensesLength && !productsLength && !filesLength;

    const licenses = useLicenses();
    const isUniversalGroup = licenses.some((license) => license.universalGroupName === props.group.name);

    // the file query only starts when the dialog opens, so an unfinished or failed count still
    // reads as zero. treating either as deletable would offer Delete for a group full of assets.
    const isCountingAssets = isLoadingFiles || isLoadingProducts;
    const hasAssetCheckFailed = isFilesError || isProductsError;
    const canBeDeleted = !isCountingAssets && !hasAssetCheckFailed && isGroupEmpty && !isUniversalGroup;
    const isDefaultGroup = workspace?.defaultGroupId === props.group.id;

    const onSubmit = async () => {
        await dispatch(deleteGroup({ id: props.group.id }))
            .unwrap()
            .then(() => showSuccessToast(t("modals:deleteGroup.toast.groupDeletedSuccess")))
            .catch(({ message = t("modals:deleteGroup.toast.groupDeletedError") }) => {
                showErrorToast(message);
            })
            .finally(() => {
                props.onClose();
            });
    };
    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && canBeDeleted) {
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            render={({ handleSubmit, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        open={props.open}
                        title={t("modals:deleteGroup.title")}
                        onClose={props.onClose}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        endButton={
                            <DangerButton onClick={form.submit} disabled={!canBeDeleted}>
                                {t("common:button.delete")}
                            </DangerButton>
                        }
                    >
                        {isCountingAssets ? <CircularProgressBox size="small" /> : null}

                        {canBeDeleted ? <Typography>{t("modals:deleteGroup.description")}</Typography> : null}

                        {canBeDeleted && isDefaultGroup ? (
                            <CustomAlert severity="warning">
                                <Typography>{t("modals:deleteGroup.defaultGroupWarning")}</Typography>
                            </CustomAlert>
                        ) : null}

                        {!isCountingAssets && isUniversalGroup ? (
                            <CustomAlert severity="warning">
                                <Typography>{t("modals:deleteGroup.cannotDeleteUniversalLicenses")}</Typography>
                            </CustomAlert>
                        ) : null}

                        {!isCountingAssets && hasAssetCheckFailed ? (
                            <CustomAlert severity="error">
                                <Typography>{t("modals:deleteGroup.assetCheckFailed")}</Typography>
                            </CustomAlert>
                        ) : null}

                        {!isCountingAssets && !hasAssetCheckFailed && !isGroupEmpty ? (
                            <CustomAlert severity="warning">
                                <Typography>{t("modals:deleteGroup.cannotDeleteReason")}</Typography>

                                <List sx={{ listStyleType: "disc", pl: 4 }}>
                                    {licensesLength ? (
                                        <ListItem sx={{ display: "list-item" }}>
                                            <Typography>
                                                {t("modals:deleteGroup.licenseCount", { count: licensesLength })}
                                            </Typography>
                                        </ListItem>
                                    ) : null}
                                    {productsLength ? (
                                        <ListItem sx={{ display: "list-item" }}>
                                            <Typography>
                                                {t("modals:deleteGroup.productCount", { count: productsLength })}
                                            </Typography>
                                        </ListItem>
                                    ) : null}
                                    {filesLength ? (
                                        <ListItem sx={{ display: "list-item" }}>
                                            <Typography>{t("modals:deleteGroup.fileCount", { count: filesLength })}</Typography>
                                        </ListItem>
                                    ) : null}
                                </List>
                            </CustomAlert>
                        ) : null}
                    </Modal>
                </form>
            )}
        />
    );
};

export default DeleteGroupDialog;
